import { useMutation } from '@tanstack/react-query';
import { useEmberService } from '@qonto/react-migration-toolkit/react/hooks';
import { useToast } from '@repo/design-system-kit';
import { useNavigate } from '@repo/poly-hooks/routing';
import { useSegment } from '@repo/poly-hooks/segment';
import { useSession } from '@repo/poly-hooks/session';
import { useSentry } from '@repo/shared-kit/sentry';
import { shouldSendToSentry } from '@repo/shared-kit/utils';
import { useIntl } from 'react-intl';
import { sensitiveAction } from '@repo/sca';
// oxlint-disable-next-line no-duplicate-imports
import type { UserData } from '@repo/sca';
import { useZendeskLocalization } from '@repo/poly-hooks/zendesk-localization';
import type { NetworkManagerError } from 'register-partners-js/services/network-manager';
import { HTTP_STATUS_CODES } from 'register-partners-js/constants/http-status-codes';

interface AuthenticatePayload {
  code: string;
  email: string;
}

interface NetworkManagerService {
  scaHeader: unknown;
}

interface UseEmailConfirmationMutationOptions {
  redirectionId: string;
  isQontoInvoicingFlow?: boolean;
  onSuccess?: () => void;
}

export function useEmailConfirmationMutation(options: UseEmailConfirmationMutationOptions) {
  const session = useSession();
  // oxlint-disable-next-line typescript/consistent-type-assertions -- bridging untyped Ember service during migration
  const networkManager = useEmberService('network-manager') as unknown as NetworkManagerService;
  const segment = useSegment();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { formatMessage } = useIntl();
  const sentry = useSentry();
  const zendeskLocalization = useZendeskLocalization();

  return useMutation({
    mutationFn: async ({ code, email }: AuthenticatePayload) => {
      const userData: UserData = {
        email,
        mfaEnabled: false,
        mfaMethod: undefined,
        phoneNumber: undefined,
      };

      const result = await sensitiveAction({
        action: async () => {
          await session.authenticate('authenticator:custom', { code, email });
        },
        onScaSuccess: (params: unknown) => {
          networkManager.scaHeader = params;
        },
        hostFaqService: {
          getLocalizedArticle: articleSlug => zendeskLocalization.getLocalizedArticle(articleSlug),
          getLocalizedHelpCenter: zendeskLocalization.getLocalizedHelpCenter,
        },
        user: userData,
      });

      return result;
    },
    onSuccess: result => {
      if (result.status === 'cancelled') return;

      segment.track('onboarding_partner_email_verified', {
        ...(options.isQontoInvoicingFlow && { flow: 'fr_embed_qf' }),
      });
      segment.track('onboarding_partner_credentials_succeeded', {
        ...(options.isQontoInvoicingFlow && { flow: 'fr_embed_qf' }),
      });
      options.onSuccess?.();
    },
    onError: (error: NetworkManagerError) => {
      const status = error.status;

      switch (status) {
        case HTTP_STATUS_CODES.UNAUTHORIZED:
        case HTTP_STATUS_CODES.TOO_MANY_REQUESTS:
          break;

        case HTTP_STATUS_CODES.NOT_FOUND:
          segment.track('onboarding_partner_email_verified', {
            ...(options.isQontoInvoicingFlow && { flow: 'fr_embed_qf' }),
          });
          navigate(
            `/${options.redirectionId}/${options.isQontoInvoicingFlow ? 'password-creation' : 'consent'}`
          );
          break;

        default:
          showToast({
            type: 'error',
            text: formatMessage({ id: 'confirmation-code.errors.internal_error' }),
          });
          if (shouldSendToSentry(error)) {
            sentry.captureException(error);
          }
          break;
      }
    },
  });
}
